import type {TriggerIntegrationConfigComponentProps} from '@cohort/merchants/apps';
import Input from '@cohort/merchants/components/form/input/Input';
import {useEffect, useState} from 'react';
import {useController} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

const TriggerIntegrationDateRangeInput: React.FC<
  {
    endDateName: `trigger.triggerIntegrationConfig.${string}`;
    startDateName: `trigger.triggerIntegrationConfig.${string}`;
    startDateTimestampName?: `trigger.triggerIntegrationConfig.${string}`;
    endDateTimestampName?: `trigger.triggerIntegrationConfig.${string}`;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } & TriggerIntegrationConfigComponentProps<any>
> = ({
  startDateName,
  endDateName,
  startDateTimestampName,
  endDateTimestampName,
  formReturn: {register, control, setValue},
}) => {
  const {t} = useTranslation('components', {
    keyPrefix: 'triggerIntegrations',
  });

  const {field: startDate} = useController({
    control,
    name: startDateName,
  });
  const {field: endDate} = useController({
    control,
    name: endDateName,
  });
  const [minEndDate, setMinEndDate] = useState(startDate.value);

  useEffect(() => {
    setMinEndDate(startDate.value);
  }, [startDate.value]);

  // Helper to format Date to yyyy-MM-ddThh:mm for datetime-local input
  function formatDateTimeLocal(value: unknown): string {
    if (!value) return '';
    const date = typeof value === 'string' ? new Date(value) : value instanceof Date ? value : null;
    if (!date || isNaN(date.getTime())) return '';
    const pad = (n: number): string => n.toString().padStart(2, '0');
    return (
      date.getFullYear() +
      '-' +
      pad(date.getMonth() + 1) +
      '-' +
      pad(date.getDate()) +
      'T' +
      pad(date.getHours()) +
      ':' +
      pad(date.getMinutes())
    );
  }

  return (
    <div className="flex flex-row gap-4">
      <Input
        name={startDateName}
        label={t('labelActivityStartDate')}
        register={register}
        control={control}
        type="datetime-local"
        value={formatDateTimeLocal(startDate.value)}
        onChange={e => {
          startDate.onChange(e.target.value);
          if (startDateTimestampName) {
            setValue(startDateTimestampName, new Date(e.target.value).getTime());
          }
        }}
      />
      <Input
        name={endDateName}
        label={t('labelActivityEndDate')}
        register={register}
        control={control}
        type="datetime-local"
        min={formatDateTimeLocal(minEndDate)}
        value={formatDateTimeLocal(endDate.value)}
        onChange={e => {
          endDate.onChange(e.target.value);
          if (endDateTimestampName) {
            setValue(endDateTimestampName, new Date(e.target.value).getTime());
          }
        }}
      />
    </div>
  );
};

export default TriggerIntegrationDateRangeInput;
